/**
 * Dialog permessi moduli: l'admin attiva/disattiva le voci di menu
 * dei portali Agente e Magazzino per il singolo utente.
 */
import React, { useState, useEffect } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

export const AGENT_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "new-order", label: "Nuovo ordine" },
  { key: "offers", label: "Offerte" },
  { key: "dedicated-offers", label: "Offerte Dedicate" },
  { key: "approvals", label: "Da approvare" },
  { key: "orders", label: "Ordini" },
  { key: "documents", label: "Documenti" },
  { key: "customers", label: "Clienti" },
  { key: "statements", label: "Scadenziario" },
  { key: "commissions", label: "Le mie provvigioni" },
  { key: "tutorials", label: "Guide" },
];

export const WAREHOUSE_MODULES = [
  { key: "orders", label: "Ordini" },
  { key: "inventory", label: "Carichi Magazzino" },
  { key: "lots", label: "Giacenze & Partite" },
  { key: "wms", label: "WMS · Giacenze" },
  { key: "wms-movimenti", label: "WMS · Movimenti" },
  { key: "documents", label: "Documenti" },
  { key: "statements", label: "Scadenziario" },
  { key: "customers", label: "Clienti" },
  { key: "pricelists", label: "Listini" },
  { key: "offers", label: "Offerte" },
  { key: "dedicated-offers", label: "Offerte Dedicate" },
  { key: "suppliers", label: "Fornitori" },
  { key: "supplier-orders", label: "Ordini Fornitori" },
  { key: "stats", label: "Statistiche" },
  { key: "tutorials", label: "Guide" },
];

export default function ModulePermissionsDialog({ open, onClose, title, modules, initialDisabled, onSave }) {
  const [disabled, setDisabled] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDisabled(new Set(initialDisabled || []));
  }, [open, initialDisabled]);

  const toggle = (key) => setDisabled((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await onSave([...disabled]);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto" data-testid="module-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-[#8A1C2A]" /> Moduli — {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Disattiva le voci di menu che questo utente non deve vedere. Le modifiche si applicano al prossimo caricamento del portale.
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
          {modules.map((m) => {
            const active = !disabled.has(m.key);
            return (
              <div key={m.key} className={`flex items-center justify-between px-3 py-2.5 ${active ? "bg-white" : "bg-stone-50"}`}>
                <span className={`text-sm ${active ? "text-stone-900 font-medium" : "text-stone-400 line-through"}`}>{m.label}</span>
                <Switch checked={active} onCheckedChange={() => toggle(m.key)} data-testid={`module-toggle-${m.key}`} />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="module-permissions-save">
            {saving ? "Salvataggio..." : "Salva permessi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
